import React, { useEffect, useState } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import './LevelUpAnimation.css';

const LevelUpAnimation = ({ oldLevel, newLevel, currentEvolution, nextEvolution, isEvolution, onComplete, playerId }) => {
  const { t } = useLocalization();

  // t() returns the raw key while translations load - fall back to English
  const tf = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const [phase, setPhase] = useState('initial');
  const [showOldDragon, setShowOldDragon] = useState(true);
  const [showNewDragon, setShowNewDragon] = useState(false);
  const [currentDragonImage, setCurrentDragonImage] = useState(null);
  const [nextDragonImage, setNextDragonImage] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  // Fetch dragon images with authentication
  const fetchDragonImage = async (evolution) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available');
      return null;
    }

    try {
      const response = await fetch(`/app/api/dragon-lair/dragon-image?player_id=${playerId}&evolution=${evolution}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } else {
        console.error(`Failed to fetch ${evolution} dragon image:`, response.status);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching ${evolution} dragon image:`, error);
      return null;
    }
  };

  useEffect(() => {
    // Fetch the dragon images for evolution animation
    if (isEvolution && playerId) {
      console.log('Evolution animation setup:', {
        currentEvolution,
        nextEvolution,
        playerId
      });
      
      // Fetch both dragon images
      const loadImages = async () => {
        if (currentEvolution) {
          const currentImg = await fetchDragonImage(currentEvolution);
          setCurrentDragonImage(currentImg);
        }
        if (nextEvolution) {
          const nextImg = await fetchDragonImage(nextEvolution);
          setNextDragonImage(nextImg);
        }
      };
      
      loadImages();
    }

    // Start animation sequence
    const startAnimation = setTimeout(() => {
      setPhase('animating');
      
      if (isEvolution) {
        // For evolution: fade out old dragon/egg, fade in new dragon
        // Wait 1 second, then start fade out
        setTimeout(() => {
          setShowOldDragon(false);  // Start 4s fade out
          // Wait for fade out to complete (4s) plus a small pause
          setTimeout(() => {
            setShowNewDragon(true);  // Start 4s fade in
            // Wait for fade in to complete (4s) plus buffer before finishing
            setTimeout(() => {
              setPhase('complete');
              if (onComplete) onComplete();
            }, 5000);  // 4s fade in + 1s buffer
          }, 4500);  // 4s fade out + 0.5s pause
        }, 1000);  // Initial delay before starting
      } else {
        // For level up: just animate the level number
        setTimeout(() => {
          setPhase('complete');
          if (onComplete) onComplete();
        }, 3000);  // Make level up animation longer too
      }
    }, 100);

    // Play sound effect if available
    if (isEvolution || newLevel > oldLevel) {
      // Use level-up.mp3 for both evolution and level up (evolution.mp3 doesn't exist yet)
      const audioUrl = '/api/audio/level-up.mp3';
      const audio = new Audio(audioUrl);
      audio.play().catch(e => {
        // Silently handle missing audio files
        console.log('Audio not available:', audioUrl);
      });
    }

    return () => {
      clearTimeout(startAnimation);
      // Clean up blob URLs
      if (currentDragonImage && currentDragonImage.startsWith('blob:')) {
        URL.revokeObjectURL(currentDragonImage);
      }
      if (nextDragonImage && nextDragonImage.startsWith('blob:')) {
        URL.revokeObjectURL(nextDragonImage);
      }
    };
  }, [isEvolution, onComplete, currentEvolution, nextEvolution, playerId]);

  return (
    <div className={`levelup-animation ${phase}`}>
      {isEvolution ? (
        <div className="evolution-container">
          <h1 className="evolution-title">
            {currentEvolution === 'egg' ? 'Your Dragon is Hatching!' : 'Dragon Evolution!'}
          </h1>
          <div className="dragon-transition">
            {showOldDragon && currentDragonImage && (
              <div className="dragon-image fade-out">
                <img 
                  src={currentDragonImage} 
                  alt={`${currentEvolution} dragon`}
                />
              </div>
            )}
            {showNewDragon && nextDragonImage && (
              <div className="dragon-image fade-in">
                <img 
                  src={nextDragonImage} 
                  alt={`${nextEvolution} dragon`}
                />
              </div>
            )}
            {/* Show loading message if images are still being fetched */}
            {!currentDragonImage && !nextDragonImage && showOldDragon && (
              <div className="loading-dragon">
                <div className="spinner"></div>
                <p>{tf('levelUp.loadingDragon', 'Loading dragon...')}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="levelup-container">
          <div className="level-icon">⭐</div>
          <h1 className="levelup-title">{tf('levelUp.title', 'Level Up!')}</h1>
          <div className="level-transition">
            <span className={`level-number old ${phase === 'animating' ? 'fade-out' : ''}`}>
              {oldLevel}
            </span>
            <span className="level-arrow">→</span>
            <span className={`level-number new ${phase === 'animating' ? 'fade-in' : ''}`}>
              {newLevel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelUpAnimation;